export default (
    date: Date,
    primaryGoal: string | null,
    secondaryGoal: string | null,
    gender: Gender | null,
    bodyWeightKg: number | null,
    bodyFatPercent: number | null,
    weightLossPerWeek: number | null,
    yogaMobilityHoursPerWeek: number | null,
    walkingHoursPerWeek: number | null,
    conditioningHoursPerWeek: number | null,
    weightliftingHoursPerWeek: number | null,
    aquafitHoursPerWeek: number | null,
    muayThaiHoursPerWeek: number | null,
    proteinLevel: string | null,
    fatLevel: string | null) => ({
    step: 'PrimaryGoal',
    get SaveEnabled() {
        return this.weightLossPerWeek !== null && !this.isTargetInvalid;
    },
    get SaveVisible() {
        return this.step === 'Final';
    },
    Save() {
        (window as any).htmx.trigger('#targets-form', 'submit');
    },
    get NextEnabled() {
        if (this.step === 'PrimaryGoal') {
            return this.primaryGoal !== '';
        }

        if (this.step === 'SecondaryGoal') {
            return this.secondaryGoal !== '';
        }

        if (this.step === 'Measurements') {
            return this.sex !== null && this.bodyWeight > 0 && this.bodyFatPercent > 0;
        }

        return false;
    },
    get NextVisible() {
        return this.step !== 'Final';
    },

    NextStep() {
        this.$el.blur();

        if (this.step === 'PrimaryGoal') {
            if (this.areAnySecondaryGoalsVisible) {
                this.step = 'SecondaryGoal';
            } else {
                this.step = 'Final';
            }
            return;
        }

        if (this.step === 'SecondaryGoal') {
            this.step = 'Final';
            return;
        }

        if (this.step === 'Measurements') {
            this.step = 'Final';
            return;
        }
    },

    get BackEnabled() {
        return this.step !== 'PrimaryGoal';
    },
    get BackVisible() {
        return this.step !== 'PrimaryGoal';
    },

    BackStep() {
        this.$el.blur();

        if (this.step === 'Final') {
            if (this.areAnySecondaryGoalsVisible) {
                this.step = 'SecondaryGoal';
            } else {
                this.step = 'PrimaryGoal';
            }
            return;
        }

        if (this.step === 'Measurements') {
            if (this.areAnySecondaryGoalsVisible) {
                this.step = 'SecondaryGoal';
            } else {
                this.step = 'PrimaryGoal';
            }
            return;
        }

        if (this.step === 'SecondaryGoal') {
            this.step = 'PrimaryGoal';
            return;
        }
    },

    get kcalTarget() {
        return this.round2dp(this.tdeePhuketFit - this.getKcalByWeight(this.getWeightLossByPercent()) / 7);
    },

    // --- Feasibility checks ---
    get feasibilityError(): string | null {
        if (this.dailyTargetsEditable) {
            return null;
        }

        const proteinG = this.calculateProtein();
        const proteinKcal = proteinG * 4;
        const C = this.kcalTarget;

        // Check 1: Protein exceeds total calories
        if (C <= proteinKcal) {
            return 'Protein alone exceeds your calorie target. Choose a smaller deficit or a lower protein level.';
        }

        if (this.proteinLevel === 'Keto') {
            // Not a valid protein level, but guard anyway
            return null;
        }

        if (this.fatLevel === 'Keto') {
            const npKcal = C - proteinKcal;

            // Check 3: Keto carb minimum
            if (npKcal < 80) {
                return 'Not enough non-protein calories for keto. Choose a smaller deficit or a lower protein level.';
            }

            // Calculate keto values to check fat
            const carbKcalCandidate = npKcal * 0.10;
            const carbGCandidate = carbKcalCandidate / 4;
            const carbG = Math.max(20, Math.min(carbGCandidate, 50));
            const carbKcal = carbG * 4;
            const fatKcal = C - proteinKcal - carbKcal;

            // Check 4: Keto fat minimum
            if (fatKcal < 9) {
                return 'Not enough calories remaining for fat on keto. Choose a smaller deficit or a lower protein level.';
            }
        } else {
            // Non-keto: check remaining calories for carbs
            const fatG = this.calculateFat();
            const fatKcal = fatG * 9;
            const remainingKcal = C - proteinKcal - fatKcal;

            // Check 2: Non-keto carb minimum (20g = 80 kcal)
            if (remainingKcal < 80) {
                return 'Not enough calories remaining for carbs. Choose a smaller deficit or a lower protein/fat level.';
            }
        }

        return null;
    },

    get isTargetInvalid() {
        return this.feasibilityError !== null;
    },

    getKcalByWeight(weightKg: number) {
        return this.round2dp(weightKg * 7700);
    },
    getWeightLossByPercent() {
        if (this.weightLossPerWeek > 1) {
            return -0.25;
        }

        return this.round2dp(this.bodyWeight * this.weightLossPerWeek);
    },
    get leanBodyMass() {
        return this.round2dp(this.bodyWeight - (this.bodyWeight * this.bodyFatPercent / 100));
    },
    manualKcalTarget: null as number | null,
    manualProtein: null as number | null,
    manualCarbs: null as number | null,
    manualFat: null as number | null,
    get macros() {
        if (this.dailyTargetsEditable) {
            if (this.manualKcalTarget !== null) {
                return {
                    protein: this.manualProtein,
                    proteinPercent: Math.round((this.manualProtein * 4) / this.manualKcalTarget * 100),
                    fat: this.manualFat,
                    fatPercent: Math.round((this.manualFat * 9) / this.manualKcalTarget * 100),
                    carbs: this.manualCarbs,
                    carbsPercent: Math.round((this.manualCarbs * 4) / this.manualKcalTarget * 100)
                }
            }
        }

        var protein = this.calculateProtein();
        var fat = this.calculateFat();
        var carbs = this.calculateCarbs(protein, fat);

        const proteinKcal = protein * 4;
        const fatKcal = fat * 9;
        const carbsKcal = carbs * 4;

        const proteinPercent = Math.round(proteinKcal / this.kcalTarget * 100);
        const fatPercent = Math.round(fatKcal / this.kcalTarget * 100);
        const carbsPercent = Math.round(carbsKcal / this.kcalTarget * 100);

        if (this.dailyTargetsEditable) {
            this.manualKcalTarget = Math.round(this.kcalTarget);
            this.manualProtein = Math.round(protein);
            this.manualCarbs = Math.round(carbs);
            this.manualFat = Math.round(fat);
        }

        return {
            protein: protein,
            proteinPercent: proteinPercent,
            fat: fat,
            fatPercent: fatPercent,
            carbs: carbs,
            carbsPercent: carbsPercent,
        };
    },
    get isDeficit() {
        return this.kcalTarget < this.tdeePhuketFit;
    },
    get deficitMultiplier() {
        return this.isDeficit ? 1.10 : 1.0;
    },
    calculateProteinBase() {
        const bw = this.bodyWeight;
        const bf = this.bodyFatPercent / 100;
        const lbm = this.leanBodyMass;

        switch (this.proteinLevel) {
            case 'MinProtein':
                return bf <= 0.30 ? Math.max(1.6 * bw, 2.2 * lbm) : 2.2 * lbm;
            case 'MedProtein':
                return Math.min(Math.max(1.8 * bw, 2.4 * lbm), 2.2 * bw);
            case 'HighProtein':
                return 2.2 * bw;
            default:
                return bf <= 0.30 ? Math.max(1.6 * bw, 2.2 * lbm) : 2.2 * lbm;
        }
    },
    calculateProtein() {
        return this.calculateProteinBase() * this.deficitMultiplier;
    },
    getProteinFormula(level: string): string {
        const bw = this.bodyWeight;
        const bf = this.bodyFatPercent / 100;
        const lbm = this.leanBodyMass;
        const suffix = this.isDeficit ? ' \u00d7 1.1' : '';

        switch (level) {
            case 'MinProtein':
                if (bf <= 0.30) {
                    return (1.6 * bw >= 2.2 * lbm ? '1.6 \u00d7 BW' : '2.2 \u00d7 LBM') + suffix;
                }
                return '2.2 \u00d7 LBM' + suffix;
            case 'MedProtein': {
                const inner = Math.max(1.8 * bw, 2.4 * lbm);
                if (inner >= 2.2 * bw) {
                    return '2.2 \u00d7 BW' + suffix;
                }
                return (1.8 * bw >= 2.4 * lbm ? '1.8 \u00d7 BW' : '2.4 \u00d7 LBM') + suffix;
            }
            case 'HighProtein':
                return '2.2 \u00d7 BW' + suffix;
            default:
                return '';
        }
    },
    getProteinGramsForLevel(level: string): number {
        const bw = this.bodyWeight;
        const bf = this.bodyFatPercent / 100;
        const lbm = this.leanBodyMass;

        let base: number;
        switch (level) {
            case 'MinProtein':
                base = bf <= 0.30 ? Math.max(1.6 * bw, 2.2 * lbm) : 2.2 * lbm;
                break;
            case 'MedProtein':
                base = Math.min(Math.max(1.8 * bw, 2.4 * lbm), 2.2 * bw);
                break;
            case 'HighProtein':
                base = 2.2 * bw;
                break;
            default:
                base = bf <= 0.30 ? Math.max(1.6 * bw, 2.2 * lbm) : 2.2 * lbm;
        }
        return Math.round(base * this.deficitMultiplier);
    },
    calculateFat() {
        const C = this.kcalTarget;

        switch (this.fatLevel) {
            case 'MinFat':
                return Math.min((C * 0.15) / 9, this.bodyWeight * 0.66);
            case 'LowFat':
                return this.bodyWeight * 0.66;
            case 'MedFat':
                return this.bodyWeight * 0.80;
            case 'HighFat':
                return this.bodyWeight * 1.1;
            case 'Keto': {
                // Keto: carbs first, fat fills remainder
                const proteinKcal = this.calculateProtein() * 4;
                const carbG = this.calculateKetoCarbs();
                const carbKcal = carbG * 4;
                const fatKcal = C - proteinKcal - carbKcal;
                return fatKcal / 9;
            }
            default:
                return this.bodyWeight * 0.66;
        }
    },
    calculateKetoCarbs() {
        const C = this.kcalTarget;
        const proteinKcal = this.calculateProtein() * 4;
        const npKcal = C - proteinKcal;
        const carbKcalCandidate = npKcal * 0.10;
        const carbGCandidate = carbKcalCandidate / 4;
        return Math.max(20, Math.min(carbGCandidate, 50));
    },
    calculateCarbs(protein: number, fat: number) {
        if (this.fatLevel === 'Keto') {
            return this.calculateKetoCarbs();
        }

        const kcal = this.kcalTarget;
        return (kcal - ((protein * 4) + (fat * 9))) / 4;
    },
    get protein() {
        return this.macros.proteinPercent;
    },
    get carbs() {
        return this.macros.carbsPercent;
    },
    get fat() {
        return this.macros.fatPercent;
    },
    startDate: toDateString(date),
    lastChange: null as string | null,
    lastProteinRatio: null as number | null,
    lastCarbsRatio: null as number | null,
    lastFatRatio: null as number | null,
    get proteinGrams() {
        if (this.isTargetInvalid && !this.dailyTargetsEditable) {
            return 0;
        }
        return Math.round(this.kcalTarget * this.protein / 100 / 4);
    },
    get carbsGrams() {
        if (this.isTargetInvalid && !this.dailyTargetsEditable) {
            return 0;
        }
        return Math.round(this.kcalTarget * this.carbs / 100 / 4);
    },
    get fatGrams() {
        if (this.isTargetInvalid && !this.dailyTargetsEditable) {
            return 0;
        }
        return Math.round(this.kcalTarget * this.fat / 100 / 9);
    },
    adjustTargets(changed: 'protein' | 'carbs' | 'fat') {

        if (this.lastChange !== changed) {
            this.lastProteinRatio = this.protein / 100;
            this.lastCarbsRatio = this.carbs / 100;
            this.lastFatRatio = this.fat / 100;
            this.lastChange = changed;
        }

        // Ensure values are treated as numbers and rounded
        this.protein = Number(this.protein);
        this.carbs =Number(this.carbs);
        this.fat = Number(this.fat);

        // Calculate the new total and the remaining percentages
        const newTotal = 100 - this[changed];

        if (changed === 'protein') {
            this.carbs = newTotal * (this.lastCarbsRatio / (this.lastCarbsRatio + this.lastFatRatio));
            this.fat = newTotal * (this.lastFatRatio / (this.lastCarbsRatio + this.lastFatRatio));

            let carbDecimal = this.carbs - Math.floor(this.carbs);
            let fatDecimal = this.fat - Math.floor(this.fat);

            this.carbs = carbDecimal > fatDecimal ? Math.ceil(this.carbs) : Math.floor(this.carbs);
            this.fat = fatDecimal > carbDecimal ? Math.ceil(this.fat) : Math.floor(this.fat);

            if (carbDecimal > 0 && carbDecimal === fatDecimal) {
                this.carbs++;
            }
        } else if (changed === 'carbs') {
            this.protein = newTotal * (this.lastProteinRatio / (this.lastProteinRatio + this.lastFatRatio));
            this.fat = newTotal * (this.lastFatRatio / (this.lastProteinRatio + this.lastFatRatio));

            let proteinDecimal = this.protein - Math.floor(this.protein);
            let fatDecimal = this.fat - Math.floor(this.fat);

            this.protein = proteinDecimal > fatDecimal ? Math.ceil(this.protein) : Math.floor(this.protein);
            this.fat = fatDecimal > proteinDecimal ? Math.ceil(this.fat) : Math.floor(this.fat);

            if (proteinDecimal > 0 && proteinDecimal === fatDecimal) {
                this.protein++;
            }
        } else if (changed === 'fat') {
            this.protein = newTotal * (this.lastProteinRatio / (this.lastProteinRatio + this.lastCarbsRatio));
            this.carbs = newTotal * (this.lastCarbsRatio / (this.lastProteinRatio + this.lastCarbsRatio));

            let proteinDecimal = this.protein - Math.floor(this.protein);
            let carbDecimal = this.carbs - Math.floor(this.carbs);

            this.protein = proteinDecimal > carbDecimal ? Math.ceil(this.protein) : Math.floor(this.protein);
            this.carbs = carbDecimal > proteinDecimal ? Math.ceil(this.carbs) : Math.floor(this.carbs);

            if (proteinDecimal > 0 && proteinDecimal === carbDecimal) {
                this.protein++;
            }
        }
    },
    primaryGoal: primaryGoal ?? '',
    secondaryGoal: secondaryGoal ?? '',
    isSecondaryGoalVisible(goal: string) {
        switch (goal) {
            case 'LoseBodyWeight':
                return this.primaryGoal === 'LoseBodyFat' || this.primaryGoal === 'GeneralFitnessImprovement';

            case 'LoseBodyFat':
                return this.primaryGoal === 'GainMuscle' || this.primaryGoal === 'GeneralFitnessImprovement';

            case 'GainMuscle':
                return this.primaryGoal === 'LoseBodyFat' || this.primaryGoal === 'GeneralFitnessImprovement';

            case 'GeneralFitnessImprovement':
                return this.primaryGoal === 'LoseBodyFat' || this.primaryGoal === 'GainMuscle';

            case 'ImproveBodyShape':
                return this.primaryGoal === 'LoseBodyFat' || this.primaryGoal === 'GainMuscle' || this.primaryGoal === 'GeneralFitnessImprovement';

            case 'GainBodyWeight':
                return this.primaryGoal === 'GainMuscle' || this.primaryGoal === 'GeneralFitnessImprovement';
        }
    },
    goalsMap: {
        LoseBodyWeight: [],
        LoseBodyFat: [
            'LoseBodyWeight',
            'GainMuscle',
            'GeneralFitnessImprovement',
            'ImproveBodyShape'
        ],
        GainMuscle: [
            'LoseBodyFat',
            'GeneralFitnessImprovement',
            'ImproveBodyShape',
            'GainBodyWeight'
        ],
        GeneralFitnessImprovement: [
            'LoseBodyWeight',
            'LoseBodyFat',
            'GainMuscle',
            'ImproveBodyShape',
            'GainBodyWeight'
        ],
        ImproveBodyShape: [],
        GainBodyWeight: []
    } as Record<string, string[]>,
    weightLossPerWeekMap: [
        {
            PrimaryGoal: 'LoseBodyWeight',
            NotRecommendedTargets: [0, 0.005, 0.01],
            RecommendedTargets: [0.015, 0.02]
        },
        {
            PrimaryGoal: 'LoseBodyFat',
            SecondaryGoal: 'LoseBodyWeight',
            NotRecommendedTargets: [0, 0.02],
            RecommendedTargets: [0.005, 0.01, 0.015]
        },
        {
            PrimaryGoal: 'LoseBodyFat',
            SecondaryGoal: 'GainMuscle',
            NotRecommendedTargets: [0.015, 0.02],
            RecommendedTargets: [0, 0.005, 0.01]
        },
        {
            PrimaryGoal: 'LoseBodyFat',
            SecondaryGoal: 'ImproveBodyShape',
            NotRecommendedTargets: [0.005, 0.01, 0.015, 0.02],
            RecommendedTargets: [0]
        },
        {
            PrimaryGoal: 'GainMuscle',
            SecondaryGoal: 'LoseBodyFat',
            NotRecommendedTargets: [0.015, 0.02],
            RecommendedTargets: [0, 0.005, 0.01]
        },
        {
            PrimaryGoal: 'GainMuscle',
            SecondaryGoal: 'GeneralFitnessImprovement',
            NotRecommendedTargets: [0.015, 0.02],
            RecommendedTargets: [0, 0.005, 0.01]
        },
        {
            PrimaryGoal: 'GainMuscle',
            SecondaryGoal: 'ImproveBodyShape',
            NotRecommendedTargets: [0.005, 0.01, 0.015, 0.02],
            RecommendedTargets: [0]
        },
        {
            PrimaryGoal: 'GainMuscle',
            SecondaryGoal: 'GainBodyWeight',
            NotRecommendedTargets: [],
            RecommendedTargets: [1.0025]
        },
        {
            PrimaryGoal: 'GeneralFitnessImprovement',
            SecondaryGoal: 'LoseBodyWeight',
            NotRecommendedTargets: [0.01, 0.015],
            RecommendedTargets: [0, 0.005, 0.02]
        },
        {
            PrimaryGoal: 'GeneralFitnessImprovement',
            SecondaryGoal: 'LoseBodyFat',
            NotRecommendedTargets: [0.015, 0.02],
            RecommendedTargets: [0, 0.005, 0.01]
        },
        {
            PrimaryGoal: 'GeneralFitnessImprovement',
            SecondaryGoal: 'GainMuscle',
            NotRecommendedTargets: [0.01, 0.015, 0.02],
            RecommendedTargets: [0, 0.005]
        },
        {
            PrimaryGoal: 'GeneralFitnessImprovement',
            SecondaryGoal: 'ImproveBodyShape',
            NotRecommendedTargets: [0.005, 0.01, 0.015, 0.02],
            RecommendedTargets: [0]
        },
        {
            PrimaryGoal: 'GeneralFitnessImprovement',
            SecondaryGoal: 'GainBodyWeight',
            NotRecommendedTargets: [],
            RecommendedTargets: [1.0025]
        },
        {
            PrimaryGoal: 'ImproveBodyShape',
            NotRecommendedTargets: [0.005, 0.01, 0.015, 0.02],
            RecommendedTargets: [0]
        },
        {
            PrimaryGoal: 'GainBodyWeight',
            NotRecommendedTargets: [],
            RecommendedTargets: [1.0025]
        },
    ],
    getExerciseGoalMap() {
        if (!this.primaryGoal) {
            return {
                YogaMobility: {
                    Min: 0,
                    Max: 0
                },
                ConditioningMTAquafit: {
                    Min: 0,
                    Max: 0
                },
                Weightlifting: {
                    Min: 0,
                    Max: 0
                },
                Walking: {
                    Min: 0,
                    Max: 0
                }
            }
        }

        return this.exerciseGoalMap[this.primaryGoal];
    },
    exerciseGoalMap: {
        LoseBodyWeight: {
            YogaMobility: {
                Min: 2,
                Max: 6
            },
            ConditioningMTAquafit: {
                Min: 2,
                Max: 5
            },
            Weightlifting: {
                Min: 2,
                Max: 3
            },
            Walking: {
                Min: 7,
                Max: 14
            }
        },
        LoseBodyFat: {
            YogaMobility: {
                Min: 2,
                Max: 5
            },
            ConditioningMTAquafit: {
                Min: 2,
                Max: 4
            },
            Weightlifting: {
                Min: 4,
                Max: 6
            },
            Walking: {
                Min: 7,
                Max: 7
            }
        },
        GainMuscle: {
            YogaMobility: {
                Min: 2,
                Max: 4
            },
            ConditioningMTAquafit: {
                Min: 2,
                Max: 5
            },
            Weightlifting: {
                Min: 5,
                Max: 6
            },
            Walking: {
                Min: 0,
                Max: 7
            }
        },
        GeneralFitnessImprovement: {
            YogaMobility: {
                Min: 3,
                Max: 5
            },
            ConditioningMTAquafit: {
                Min: 3,
                Max: 5
            },
            Weightlifting: {
                Min: 3,
                Max: 5
            },
            Walking: {
                Min: 4,
                Max: 7
            }
        },
        ImproveBodyShape: {
            YogaMobility: {
                Min: 2,
                Max: 4
            },
            ConditioningMTAquafit: {
                Min: 2,
                Max: 4
            },
            Weightlifting: {
                Min: 4,
                Max: 6
            },
            Walking: {
                Min: 0,
                Max: 7
            }
        },
        GainBodyWeight: {
            YogaMobility: {
                Min: 2,
                Max: 4
            },
            ConditioningMTAquafit: {
                Min: 2,
                Max: 4
            },
            Weightlifting: {
                Min: 5,
                Max: 6
            },
            Walking: {
                Min: 0,
                Max: 7
            }
        }
    },
    lastWeightLossPerWeekOptions: {
        PrimaryGoal: '',
        SecondaryGoal: '',
        Option: { NotRecommendedTargets: [] as number[], RecommendedTargets: [] as number[] }
    },
    get weightLossPerWeekOptions() {
        const primaryGoal = this.primaryGoal;
        const secondaryGoal = this.secondaryGoal;

        if (this.lastWeightLossPerWeekOptions.PrimaryGoal === primaryGoal && this.lastWeightLossPerWeekOptions.SecondaryGoal === secondaryGoal) {
            return this.lastWeightLossPerWeekOptions.Option;
        }

        for (let option of this.weightLossPerWeekMap) {
            if (option.PrimaryGoal === primaryGoal && (!option.SecondaryGoal || option.SecondaryGoal === secondaryGoal)) {
                this.lastWeightLossPerWeekOptions.PrimaryGoal = primaryGoal;
                this.lastWeightLossPerWeekOptions.SecondaryGoal = secondaryGoal;
                this.lastWeightLossPerWeekOptions.Option = option;
                return option;
            }
        }

        return { NotRecommendedTargets: [], RecommendedTargets: [] };
    },
    get areAnySecondaryGoalsVisible() {
        // just here to enable reactivity.
        const primaryGoal = this.primaryGoal;

        for (let goal in this.goalsMap) {
            if (this.isSecondaryGoalVisible(goal)) {
                return true;
            }
        }

        return false;
    },
    sex: gender,
    bodyWeight: bodyWeightKg,
    bodyFatPercent: bodyFatPercent,
    weightLossPerWeek: weightLossPerWeek,
    isWeightLossPerWeekVisible(value: number) {
        if (value > 1) {
            return this.primaryGoal === 'GainBodyWeight' || this.secondaryGoal === 'GainBodyWeight';
        }

        return this.primaryGoal !== 'GainBodyWeight' && this.secondaryGoal !== 'GainBodyWeight';
    },
    yogaMobilityHoursPerWeek: yogaMobilityHoursPerWeek ?? 0,
    get yogaMobilityKcalPerWeek() { return this.bodyWeight * this.yogaMobilityHoursPerWeek * 2.5; },
    walkingHoursPerWeek: walkingHoursPerWeek ?? 0,
    get walkingKcalPerWeek() { return this.bodyWeight * this.walkingHoursPerWeek * 3; },
    conditioningHoursPerWeek: conditioningHoursPerWeek ?? 0,
    get conditioningKcalPerWeek() { return this.bodyWeight * this.conditioningHoursPerWeek * 8; },
    weightliftingHoursPerWeek: weightliftingHoursPerWeek ?? 0,
    get weightliftingKcalPerWeek() { return this.bodyWeight * this.weightliftingHoursPerWeek * 3; },
    aquafitHoursPerWeek: aquafitHoursPerWeek ?? 0,
    get aquafitKcalPerWeek() { return this.bodyWeight * this.aquafitHoursPerWeek * 4; },
    muayThaiHoursPerWeek: muayThaiHoursPerWeek ?? 0,
    get muayThaiKcalPerWeek() { return this.bodyWeight * this.muayThaiHoursPerWeek * 10; },
    get bmr() {
        return 370 + (21.6 * (this.bodyWeight - this.bodyWeight * this.bodyFatPercent / 100));
    },
    get sedentaryBMR() {
        return this.bmr * 1.2;
    },
    get averageKcalFromExercisePerDay() {
        const totalKcalPerWeek = this.yogaMobilityKcalPerWeek + this.walkingKcalPerWeek + this.conditioningKcalPerWeek + this.weightliftingKcalPerWeek + this.aquafitKcalPerWeek + this.muayThaiKcalPerWeek;
        if (totalKcalPerWeek === 0) return 0;
        return this.round2dp(totalKcalPerWeek / 7);
    },
    get calorieDeficit() {
        return this.tdeePhuketFit - this.kcalTarget;
    },
    get tdeePhuketFit() { return this.round2dp(this.sedentaryBMR + this.averageKcalFromExercisePerDay); },
    proteinLevel: proteinLevel ?? 'MinProtein',
    fatLevel: fatLevel ?? 'LowFat',
    round2dp(value: number) { return Math.round(value * 100) / 100; },
    get kcalTargetToSave() {
        if (this.isTargetInvalid && !this.dailyTargetsEditable) {
            return 0;
        }
        this.kcalTarget;
        this.manualKcalTarget;
        return Math.round(this.dailyTargetsEditable ? this.manualKcalTarget : this.kcalTarget);
    },
    get proteinToSave() {
        if (this.isTargetInvalid && !this.dailyTargetsEditable) {
            return 0;
        }
        this.proteinGrams;
        this.manualProtein;
        return Math.round(this.dailyTargetsEditable ? this.manualProtein : this.proteinGrams);
    },
    get carbsToSave() {
        if (this.isTargetInvalid && !this.dailyTargetsEditable) {
            return 0;
        }
        this.carbsGrams;
        this.manualCarbs;
        return Math.round(this.dailyTargetsEditable ? this.manualCarbs : this.carbsGrams);
    },
    get fatToSave() {
        if (this.isTargetInvalid && !this.dailyTargetsEditable) {
            return 0;
        }
        this.fatGrams;
        this.manualFat;
        return Math.round(this.dailyTargetsEditable ? this.manualFat : this.fatGrams);
    },
    dailyTargetsEditable: false,
    manualKcalProteinPercent: null as number | null,
    manualKcalCarbsPercent: null as number | null,
    manualKcalFatPercent: null as number | null,
    manualKcalTargetChanged(evt: Event) {
        if (this.manualKcalProteinPercent === null) {
            const prevKcal = (this.manualProtein * 4) + (this.manualCarbs * 4) + (this.manualFat * 9);
            this.manualKcalProteinPercent = (this.manualProtein * 4) / prevKcal;
            this.manualKcalCarbsPercent = (this.manualCarbs * 4) / prevKcal;
            this.manualKcalFatPercent = (this.manualFat * 9) / prevKcal;
        }

        this.manualKcalTarget = parseInt((evt.target as HTMLInputElement).value)

        this.manualProtein = Math.round((this.manualKcalTarget * this.manualKcalProteinPercent) / 4);
        this.manualCarbs = Math.round((this.manualKcalTarget * this.manualKcalCarbsPercent) / 4);
        this.manualFat = Math.round((this.manualKcalTarget * this.manualKcalFatPercent) / 9);
    },
    manualProteinChanged(evt: Event) {
        this.manualProtein = parseInt((evt.target as HTMLInputElement).value)
        this.manualKcalTarget = (this.manualProtein * 4) + (this.manualCarbs * 4) + (this.manualFat * 9);
    },
    manualCarbsChanged(evt: Event) {
        this.manualCarbs = parseInt((evt.target as HTMLInputElement).value)
        this.manualKcalTarget = (this.manualProtein * 4) + (this.manualCarbs * 4) + (this.manualFat * 9);
    },
    manualFatChanged(evt: Event) {
        this.manualFat = parseInt((evt.target as HTMLInputElement).value)
        this.manualKcalTarget = (this.manualProtein * 4) + (this.manualCarbs * 4) + (this.manualFat * 9);
    },
    init() {
        this.$watch('dailyTargetsEditable', (val: boolean) => {
            this.updateManualTargets(val);
        });
    },
    updateManualTargets(dailyTargetsEditable: boolean) {
        if (dailyTargetsEditable) {
            this.manualKcalTarget = Math.round(this.kcalTarget);
            this.manualProtein = this.proteinGrams;
            this.manualCarbs = this.carbsGrams;
            this.manualFat = this.fatGrams;
            this.manualKcalProteinPercent = null;
            this.manualKcalCarbsPercent = null;
            this.manualKcalFatPercent = null;
        } else {
            this.manualKcalTarget = null;
            this.manualProtein = null;
            this.manualCarbs = null;
            this.manualFat = null;
            this.manualKcalProteinPercent = null;
            this.manualKcalCarbsPercent = null;
            this.manualKcalFatPercent = null;
        }
    }
});

function toDateString(value: Date) {
    const offset = value.getTimezoneOffset()
    const nextDate = new Date(value.getTime() - (offset * 60 * 1000))
    return nextDate.toISOString().split('T')[0];
}

enum Gender {
    Male = "Male",
    Female = "Female"
}
